import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { useFormDraft } from '@/hooks/useFormDraft';
import { Plus, Trash2 } from 'lucide-react';

const ICON_OPTIONS = ['🏊', '🔥', '🍳', '🏠', '🛠️', '🏗️', '🌿', '🚗', '🎨', '⚡', '🧱', '📦'];
const COLOR_OPTIONS = ['#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6', '#f97316'];
const STATUS_OPTIONS = ['active', 'completed', 'paused'];

export default function AddPersonalProjectModal({ open, onClose, project = null }) {
  const { t } = useTranslation();
  const addPersonalProject = useStore((s) => s.addPersonalProject);
  const updatePersonalProject = useStore((s) => s.updatePersonalProject);

  const isEdit = !!project;
  const draft = useFormDraft('addPersonalProject');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('🏊');
  const [color, setColor] = useState('#3b82f6');
  const [status, setStatus] = useState('active');
  const [deadline, setDeadline] = useState('');
  const [budget, setBudget] = useState('');
  const [items, setItems] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setDescription(project.description || '');
      setIcon(project.icon || '🏊');
      setColor(project.color || '#3b82f6');
      setStatus(project.status || 'active');
      setDeadline(project.deadline || '');
      setBudget(project.budget ? String(project.budget) : '');
      setItems(project.items?.map((i) => ({ ...i })) || []);
      setMemberIds(project.memberIds || []);
      setErrors({});
    } else if (open) {
      const saved = draft.load();
      if (saved) {
        setName(saved.name || '');
        setDescription(saved.description || '');
        setIcon(saved.icon || '🏊');
        setColor(saved.color || '#3b82f6');
        setStatus(saved.status || 'active');
        setDeadline(saved.deadline || '');
        setBudget(saved.budget || '');
        setItems(saved.items || []);
        setMemberIds(saved.memberIds || []);
      } else {
        setName('');
        setDescription('');
        setIcon('🏊');
        setColor('#3b82f6');
        setStatus('active');
        setDeadline('');
        setBudget('');
        setItems([]);
        setMemberIds([]);
      }
      setErrors({});
    }
  }, [open, project]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ name, description, icon, color, status, deadline, budget, items, memberIds });
  }, [open, isEdit, name, description, icon, color, status, deadline, budget, items, memberIds]);

  const handleAddItem = () => {
    setItems([...items, { id: crypto.randomUUID(), name: '', estimatedCost: 0, completed: false }]);
  };

  const handleUpdateItem = (idx, field, value) => {
    setItems(items.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const handleRemoveItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('personalProjects.form.nameRequired');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const data = {
      name: name.trim(),
      description: description.trim(),
      icon,
      color,
      status,
      deadline: deadline || undefined,
      budget: budget ? Number(budget) : 0,
      memberIds,
      items: items.map((item) => ({
        ...item,
        name: item.name.trim(),
        estimatedCost: Number(item.estimatedCost) || 0,
      })),
    };

    if (isEdit) {
      updatePersonalProject(project.id, data);
    } else {
      addPersonalProject(data);
    }
    draft.clear();
    onClose();
  };

  const statusLabels = {
    active: t('personalProjects.statusActive'),
    completed: t('personalProjects.statusCompleted'),
    paused: t('personalProjects.statusPaused'),
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('personalProjects.editProject') : t('personalProjects.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('personalProjects.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 scale-110'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900 scale-110' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.status')}
          </label>
          <div className="flex gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  status === s
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {statusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Deadline */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.deadline')}
          </label>
          <input type="date" className="input" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </div>

        {/* Budget */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.budget')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={budget}
              onChange={setBudget}
              className="w-full pl-10 pr-4 py-3 text-lg font-bold input"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('personalProjects.form.description')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('personalProjects.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Member association */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
        />

        {/* Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('personalProjects.items')}
            </label>
            <button
              onClick={handleAddItem}
              className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors"
            >
              <Plus size={14} />
              {t('personalProjects.addItem')}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('personalProjects.noItems')}</p>
          ) : (
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={item.id || idx} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1 text-sm"
                      placeholder={t('personalProjects.itemName')}
                      value={item.name}
                      onChange={(e) => handleUpdateItem(idx, 'name', e.target.value)}
                    />
                    <button
                      onClick={() => handleRemoveItem(idx)}
                      className="p-2 rounded-lg text-gray-400 hover:text-danger-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-400 mb-0.5 block">{t('personalProjects.itemCost')}</label>
                      <CurrencyInput
                        className="input text-sm"
                        placeholder="0"
                        value={String(item.estimatedCost || '')}
                        onChange={(v) => handleUpdateItem(idx, 'estimatedCost', v)}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer pt-4">
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={(e) => handleUpdateItem(idx, 'completed', e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                      />
                      {t('personalProjects.statusCompleted')}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
